import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import {
  Upload, User, Phone, Mail, FileText, Calendar, CheckCircle2, Loader2,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { useNavigation } from '../hooks/useMe';
import { presignAndUpload } from '../hooks/api/uploads';
import { useCreatePilotMission } from '../hooks/api/pilot-missions';

export function PilotUpload() {
  // Pilot Details
  const [pilotName, setPilotName] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  // Mission Details (industries/projects/deliverables now from API)
  const [selectedIndustryId, setSelectedIndustryId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedDeliverableId, setSelectedDeliverableId] = useState('');
  const [missionDate, setMissionDate] = useState('');
  const [flightDuration, setFlightDuration] = useState('');
  const [weatherConditions, setWeatherConditions] = useState('');

  // Files
  const [uploadedFiles, setUploadedFiles] = useState([]); // [{file, status, error}]
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const navigation = useNavigation();
  const createMission = useCreatePilotMission();
  const industries = navigation?.industries || [];
  const projects = useMemo(() => {
    const ind = industries.find((i) => i.id === selectedIndustryId);
    return ind?.projects || [];
  }, [industries, selectedIndustryId]);
  const deliverables = useMemo(() => {
    const p = projects.find((p) => p.id === selectedProjectId);
    return p?.deliverables || [];
  }, [projects, selectedProjectId]);

  const handleIndustryChange = (v) => { setSelectedIndustryId(v); setSelectedProjectId(''); setSelectedDeliverableId(''); };
  const handleProjectChange  = (v) => { setSelectedProjectId(v); setSelectedDeliverableId(''); };

  const handleFileUpload = (e) => {
    if (e.target.files) {
      const next = Array.from(e.target.files).map((file) => ({ file, status: 'queued', error: null }));
      setUploadedFiles((prev) => [...prev, ...next]);
    }
  };
  const removeFile = (index) => setUploadedFiles((prev) => prev.filter((_, i) => i !== index));

  const resetForm = () => {
    setPilotName(''); setLicenseNumber(''); setEmail(''); setPhone('');
    setSelectedIndustryId(''); setSelectedProjectId(''); setSelectedDeliverableId('');
    setMissionDate(''); setFlightDuration(''); setWeatherConditions('');
    setUploadedFiles([]); setComments('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pilotName || !licenseNumber || !email || !selectedIndustryId || !selectedProjectId || !selectedDeliverableId) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (uploadedFiles.length === 0) {
      toast.error('Please upload at least one file');
      return;
    }

    setSubmitting(true);
    let inspectionId = null;
    try {
      // 1) Atomically create the Inspection + PilotMission record
      const res = await createMission.mutateAsync({
        project_id: selectedProjectId,
        deliverable_id: selectedDeliverableId,
        pilot_name: pilotName,
        license_number: licenseNumber,
        contact_email: email,
        contact_phone: phone || undefined,
        mission_date: missionDate || undefined,
        flight_duration_minutes: flightDuration ? Number(flightDuration) : undefined,
        weather_conditions: weatherConditions || undefined,
        comments: comments || undefined,
      });
      inspectionId = res.inspection_id;
    } catch (err) {
      setSubmitting(false);
      toast.error(`Could not create mission: ${err?.message || 'unknown error'}`);
      return;
    }

    // 2) Upload each file attached to the new inspection_id
    const next = [...uploadedFiles];
    let failed = 0;
    for (let i = 0; i < next.length; i++) {
      if (next[i].status === 'done') continue;
      next[i] = { ...next[i], status: 'uploading', error: null };
      setUploadedFiles([...next]);
      try {
        await presignAndUpload(next[i].file, {
          file_type: 'raw',
          inspection_id: inspectionId,
          project_id: selectedProjectId,
          deliverable_id: selectedDeliverableId,
        });
        next[i] = { ...next[i], status: 'done' };
      } catch (err) {
        next[i] = { ...next[i], status: 'failed', error: err?.message || 'Upload failed' };
        failed++;
      }
      setUploadedFiles([...next]);
    }
    setSubmitting(false);
    if (failed === 0) {
      toast.success(`Pilot mission saved — ${next.length} file(s) submitted`);
    } else {
      toast.error(`${failed} of ${next.length} file(s) failed to upload`);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl">Pilot Data Upload</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Upload mission data and reports from field operations
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* Pilot Information Card */}
        <Card className="border-0 bg-card">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <User className="w-5 h-5 text-blue-500" />
              <span>Pilot Information</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="pilotName">Pilot Name <span className="text-red-500">*</span></Label>
                <Input id="pilotName" placeholder="Enter pilot name" value={pilotName} onChange={(e) => setPilotName(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="licenseNumber">License Number <span className="text-red-500">*</span></Label>
                <Input id="licenseNumber" placeholder="e.g., FAA-123456" value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="email" type="email" placeholder="pilot@example.com" className="pl-10" value={email} onChange={(e) => setEmail(e.target.value)} required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Contact Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="phone" type="tel" placeholder="+1 (555) 000-0000" className="pl-10" value={phone} onChange={(e) => setPhone(e.target.value)} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Mission Details Card */}
        <Card className="border-0 bg-card">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <FileText className="w-5 h-5 text-green-500" />
              <span>Mission Details</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="industry">Industry <span className="text-red-500">*</span></Label>
                <Select value={selectedIndustryId} onValueChange={handleIndustryChange}>
                  <SelectTrigger id="industry"><SelectValue placeholder="Select Industry" /></SelectTrigger>
                  <SelectContent>
                    {industries.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="project">Project <span className="text-red-500">*</span></Label>
                <Select value={selectedProjectId} onValueChange={handleProjectChange} disabled={!selectedIndustryId}>
                  <SelectTrigger id="project"><SelectValue placeholder="Select Project" /></SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="deliverable">Deliverable <span className="text-red-500">*</span></Label>
                <Select value={selectedDeliverableId} onValueChange={setSelectedDeliverableId} disabled={!selectedProjectId}>
                  <SelectTrigger id="deliverable"><SelectValue placeholder="Select Deliverable" /></SelectTrigger>
                  <SelectContent>
                    {deliverables.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="missionDate">Mission Date</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="missionDate" type="date" className="pl-10" value={missionDate} onChange={(e) => setMissionDate(e.target.value)} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="flightDuration">Flight Duration (minutes)</Label>
                <Input id="flightDuration" type="number" placeholder="e.g., 45" value={flightDuration} onChange={(e) => setFlightDuration(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="weatherConditions">Weather Conditions</Label>
                <Select value={weatherConditions} onValueChange={setWeatherConditions}>
                  <SelectTrigger id="weatherConditions"><SelectValue placeholder="Select Conditions" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="clear">Clear</SelectItem>
                    <SelectItem value="partly-cloudy">Partly Cloudy</SelectItem>
                    <SelectItem value="cloudy">Cloudy</SelectItem>
                    <SelectItem value="windy">Windy</SelectItem>
                    <SelectItem value="light-rain">Light Rain</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* File Upload Card */}
        <Card className="border-0 bg-card">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Upload className="w-5 h-5 text-purple-500" />
              <span>Upload Files</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center hover:border-muted-foreground/50 transition-colors">
              <input
                type="file" multiple onChange={handleFileUpload} className="hidden" id="pilot-file-upload"
                accept="image/*,video/*,.pdf,.doc,.docx"
              />
              <label htmlFor="pilot-file-upload" className="cursor-pointer">
                <Upload className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-foreground mb-2">Click to upload or drag and drop</p>
                <p className="text-sm text-muted-foreground">Images, Videos, PDFs, or Documents (Max 5GB per file)</p>
              </label>
            </div>

            {uploadedFiles.length > 0 && (
              <div className="space-y-2">
                <Label>Uploaded Files ({uploadedFiles.length})</Label>
                <div className="space-y-2">
                  {uploadedFiles.map((entry, index) => (
                    <div key={`${entry.file.name}-${index}`} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        {entry.status === 'uploading' ? <Loader2 className="w-4 h-4 animate-spin" />
                          : entry.status === 'done' ? <CheckCircle2 className="w-4 h-4 text-green-500" />
                          : entry.status === 'failed' ? <span className="w-4 h-4 text-red-500">⚠</span>
                          : <CheckCircle2 className="w-4 h-4 text-muted-foreground" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{entry.file.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {(entry.file.size / 1024 / 1024).toFixed(2)} MB
                            {entry.error ? ` · ${entry.error}` : ''}
                          </p>
                        </div>
                      </div>
                      {entry.status !== 'uploading' && (
                        <Button type="button" variant="ghost" size="sm"
                          onClick={() => removeFile(index)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-500/10">
                          Remove
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Additional Comments Card */}
        <Card className="border-0 bg-card">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <FileText className="w-5 h-5 text-orange-500" />
              <span>Additional Comments</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Add any additional notes, observations, or issues encountered during the mission..."
              rows={6}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              className="resize-none"
            />
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex items-center justify-end space-x-4">
          <Button type="button" variant="outline" onClick={resetForm} disabled={submitting}>
            Clear Form
          </Button>
          <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={submitting}>
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {submitting ? 'Uploading…' : 'Submit Pilot Data'}
          </Button>
        </div>
      </form>
    </div>
  );
}
